import {Anchor, Box, Text, ThemeContext} from "grommet";
import React, {useContext} from "react";
import useSWR from "swr";
import {silentFetcher} from "../utils/errors";
import {formatName} from "../utils/names";
import {useRouter} from "next/router";
import FontAwesomeIcon from "./FontAwesomeIcon";
import {
    IconArrowUpRight as IconExpand,
    IconMapPin as IconLocation,
    IconPhone,
    IconMail as IconEnvelope,
    IconUsers as IconGroup,
    IconX as IconClose
} from '@tabler/icons-react'
import {NAME_CARD_MIN_WIDTH} from "../constants/Dimensions";
import {formatAddress, getCountryFlagEmoji, isUSA, isEmailOnlyAddress} from "../utils/addresses";
import {DarkenOnHoverBox, DisplayInlineBlockBox, EnhancedDropButton} from "../utils/styled";
import CustomIcon from "./CustomIcon";
import Quickbooks from "./icons/Quickbooks";
import Button from "./Button";
import ContentLine from "./ContentLine";
import {isObjectEmpty} from "../utils/object";

const CustomerTag = ({id, uuid, onRemove = undefined, buttonProps={}, inline=false, inlineBlock=true}) => {
    const router = useRouter();
    const theme = useContext(ThemeContext);

    const { data: customerData } = useSWR(
        (uuid || id) ? `/api/customers/${uuid || id}/tag` : undefined,
        silentFetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000, // 1 minute - reduce duplicate requests
        },
    );

    const redirectToQuickbooks = () => {
        if (typeof window !== "undefined") {
            window.open(`https://app.qbo.intuit.com/app/customerdetail?nameId=${customerData.quickbooks_id}`, "_blank")
        }
    }

    const renderAddressComponent = (data) => {

        if (!data) return (<Box />);

        const address = !isObjectEmpty(data?.billing_address) ? data?.billing_address : data.address;
        const total_addresses = data?.total_addresses_count || 0;
        return (
            <Box direction="column" gap="small" pad={{vertical: 'small'}}>
                {!isEmailOnlyAddress(address) && !isObjectEmpty(address) && (<InfoLine
                    icon={IconLocation}
                    description={(
                        <Text height={0}>
                            {formatAddress({...address, breakAfterAddress2: true})}
                            {total_addresses > 1 ? (<>
                                <br/>
                                <Text size="small" color="text-weak">and {total_addresses - 1} more
                                    address{total_addresses > 2 ? 'es' : ''}</Text>
                            </>) : (<></>)}
                        </Text>
                    )}
                />)}
                {address?.phone && (<InfoLine
                    icon={IconPhone}
                    description={(<Text>{address?.phone || ''}</Text>)} />)}
                {address?.email && (<InfoLine
                    icon={IconEnvelope}
                    description={(<Text>{address?.email || ''}</Text>)} />)}
                {data?.customer_group && (
                    <InfoLine
                        icon={IconGroup}
                        description={(
                            <Box direction="row" align="center" gap="xxsmall" wrap>
                                <Text>In group </Text>
                                <Anchor href={`/customers/groups/${data.customer_group?.uuid}`}>{data.customer_group.company_name}</Anchor>
                            </Box>)} />
                )}
            </Box>
        )
    }

    const TagWrapper = ({ inlineBlock, children }) => {
        return inlineBlock ? (
            <DisplayInlineBlockBox>{children}</DisplayInlineBlockBox>
        ) : (
            children
        );
    };


    return (
        <TagWrapper inlineBlock={inlineBlock}>
            <EnhancedDropButton
                event={{section: 'customer_tag', element: 'tag', description: customerData?.uuid}}
                styles={'max-width: 100%;'}
                label={(
                    <DarkenOnHoverBox
                        background={theme.dark ? "foreground" : "light-1"}
                        border={{color: "border"}}
                        round="xxsmall"
                        theme={theme}>
                        <Box direction="row" align="center" pad="xxsmall" justify={!inlineBlock ? "between" : undefined}>
                            <Box
                                pad={{horizontal: 'xxsmall'}} direction="row" align="center" gap="xsmall">
                                <Text truncate wordBreak="keep-all">
                                    {'@'}{'\u200A'}
                                    {customerData?.company_name || formatName(customerData, true, '')}
                                </Text>
                                {!customerData && (<ContentLine width="xsmall" isLoading={true} />)}
                            </Box>
                            {onRemove && (
                                <Box pad={{right: 'xsmall'}}>
                                    <Anchor
                                        plain
                                        hoverIndicator
                                        label={(<FontAwesomeIcon SelectedIcon={IconClose} color="text-weak" size="xxxsmall" />)}
                                        onClick={() => onRemove(uuid || id)} />
                                </Box>
                            )}
                        </Box>
                    </DarkenOnHoverBox>
                )}
                plain
                hoverIndicator
                dropAlign={{top: 'bottom', left: 'left'}}
                dropProps={{border: true, margin: {vertical: 'xsmall'}, inline}}
                dropContent={customerData ? (
                    <Box pad="small" margin="small" width={{max: 'medium'}} flex={{shrink: 0}}>
                        <Box margin={{vertical: 'xsmall'}} border={{side: 'bottom'}} pad={{bottom: 'small'}} width={{min: NAME_CARD_MIN_WIDTH}}>
                            <Text size="large" weight={500}>
                                {customerData?.company_name || formatName(customerData, true, '-')}
                                {customerData && customerData.addresses && customerData.addresses.length && !isUSA(customerData?.billing_address?.country || customerData?.addresses[0]?.country) && (
                                    <>
                                        &nbsp;{getCountryFlagEmoji(customerData?.billing_address?.country || customerData?.addresses[0]?.country)}
                                    </>
                                )}
                            </Text>
                            {customerData?.company_name && (
                                <Text>
                                    {formatName(customerData, true, '-')}
                                </Text>
                            )}
                        </Box>
                        {renderAddressComponent(customerData)}
                        { customerData.quickbooks_id && customerData.has_quickbooks_linked &&
                        (
                            <Box direction="row" align="center" gap="small">
                                <Box margin="2px">
                                    <CustomIcon color="border-strong" SelectedIcon={Quickbooks} size="small"/>
                                </Box>
                                <Box>
                                    <Button
                                        event={{section: `customer_tag`, element: 'view_in_quickbooks', description: customerData?.uuid}}
                                        plain
                                        label={
                                            <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                                <Text color="text">View in QuickBooks</Text>
                                                <Box>
                                                    <FontAwesomeIcon SelectedIcon={IconExpand} size="xsmall"/>
                                                </Box>
                                            </Box>
                                        }
                                        onClick={redirectToQuickbooks}/>
                                </Box>
                            </Box>
                        )
                        }
                        <Box margin={{top: 'small'}}>
                            <Button
                                event={{section: `customer_tag`, element: 'view_profile', description: customerData?.uuid}}
                                primary
                                ctaColor="accent"
                                color="accent"
                                fill="horizontal"
                                label={(
                                    <Box direction="row" gap="small" align="center" fill="horizontal" justify="center">
                                        <Text color="text">View profile</Text>
                                    </Box>
                                )}
                                onClick={() => router.push(`/customers/${customerData?.uuid}`)}
                            />
                        </Box>
                    </Box>
                ) : (<Box />)}
                width={{max: '100%'}}
                fill={!inlineBlock ? "horizontal" : undefined}
                {...buttonProps} />
        </TagWrapper>
    );
}

export const InfoLine = ({icon, description}) => (
    <Box direction="row" align="start" gap="small">
        <Box flex={false} pad={{vertical: 'xxsmall'}}>
            <FontAwesomeIcon SelectedIcon={icon} size='xsmall' />
        </Box>
        <Box justify="end">
            {description}
        </Box>
    </Box>
);

export default CustomerTag;
